import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, DataTable, DropChip, money, shortDate, useAdminShell } from '../../components/admin/shell';

const STATUSES = [
  { value: '', label: 'ALL' }, { value: 'pending', label: 'PENDING' }, { value: 'processing', label: 'PROCESSING' },
  { value: 'completed', label: 'COMPLETED' }, { value: 'failed', label: 'FAILED' },
];

/**
 * MONEY › WITHDRAWALS — spec/sections.md §7. A cash-out completes in the request when Stripe
 * answers; one Stripe refuses for certain fails on its own and its money goes back. What is left
 * for a human is a row stuck in `processing` (`withdrawals` · approve): RESUME (same Stripe key,
 * never a second transfer), MARK COMPLETED (from the transfer Stripe made) or MARK FAILED (money
 * back, refused by the server whenever Stripe shows a transfer for it). Without the verb this is
 * the ledger.
 */
export default function WithdrawalsPage() {
  const { can } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const { refreshCounts, openPerson, canSeePeople } = useAdminShell();
  const canApprove = can('withdrawals', 'approve');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getWithdrawals(page, status);
    setLoading(false);
    if (r.success) { setRows(r.data.withdrawals || []); setTotal(r.data.total || 0); }
  }, [page, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => { const r = await adminService.getWithdrawals(1, 'pending'); if (r.success) setWaiting(r.data.total || 0); })(); }, [rows]);

  // Oct 28 2026 (audit M1): the server refuses MARK FAILED while a resume is talking to Stripe, on a
  // row under 10 minutes old, and whenever Stripe holds a transfer carrying this withdrawal's id —
  // it then says MARK COMPLETED, which this page offers straight away. A row that asked Stripe and
  // shows nothing there still needs staff to attest they checked the dashboard.
  const retry = async (w) => {
    const ok = await showConfirm({ title: 'RESUME WITHDRAWAL', message: `Ask Stripe again for ${money(w.netAmountCents)} to ${w.userEmail || 'this user'} on the same request — if the transfer already went through, it is simply recorded. If Stripe refuses for certain, the money goes back to their wallet.${w.failureReason ? ` Last failure: ${w.failureReason}` : ''}`, confirmLabel: 'RESUME', variant: 'primary', theme: 'admin' });
    if (!ok) return;
    const r = await adminService.retryWithdrawal(w.id);
    if (!r.success) await showAlert({ title: 'COULD NOT RESUME', message: r.error, type: 'error', theme: 'admin' });
    load(); refreshCounts();
  };
  const markFailed = async (w) => {
    const ok = await showConfirm({ title: 'MARK FAILED', message: `Mark ${money(w.amountCents)} to ${w.userEmail || 'this user'} as failed. The amount goes back to their balance and they are told why.`, confirmLabel: 'MARK FAILED', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    let r = await adminService.markWithdrawalFailed(w.id, 'Marked failed by staff');
    if (!r.success && /Stripe dashboard/i.test(String(r.error || ''))) {
      const sure = await showConfirm({ title: 'STRIPE WAS ALREADY ASKED', message: `${r.error}

Only continue if the Stripe dashboard shows NO transfer for this withdrawal.`, confirmLabel: 'NO TRANSFER EXISTS — MARK FAILED', cancelLabel: 'STOP', variant: 'danger', theme: 'admin' });
      if (!sure) { load(); return; }
      r = await adminService.markWithdrawalFailed(w.id, 'Marked failed by staff (no Stripe transfer found)', true);
    }
    if (!r.success && /MARK COMPLETED/.test(String(r.error || ''))) {
      const complete = await showConfirm({ title: 'STRIPE SENT IT', message: `${r.error}`, confirmLabel: 'MARK COMPLETED', cancelLabel: 'STOP', variant: 'primary', theme: 'admin' });
      if (complete) r = await adminService.markWithdrawalCompleted(w.id);
      else { load(); return; }
    }
    if (!r.success) await showAlert({ title: 'COULD NOT MARK FAILED', message: r.error, type: 'error', theme: 'admin' });
    load(); refreshCounts();
  };
  const markCompleted = async (w) => {
    const ok = await showConfirm({ title: 'MARK COMPLETED', message: `Find the Stripe transfer for ${money(w.netAmountCents)} to ${w.userEmail || 'this user'} and record it. Stripe's transfer is checked term by term (amount, account, this withdrawal's id); no money moves.`, confirmLabel: 'MARK COMPLETED', variant: 'primary', theme: 'admin' });
    if (!ok) return;
    const r = await adminService.markWithdrawalCompleted(w.id);
    if (!r.success) await showAlert({ title: 'COULD NOT MARK COMPLETED', message: r.error, type: 'error', theme: 'admin' });
    load(); refreshCounts();
  };

  const meta = waiting == null ? 'Payouts leaving the platform.' : waiting === 0 ? 'Payouts leaving the platform. Nothing is waiting on a human.' : `Payouts leaving the platform. ${waiting} ${waiting === 1 ? 'is' : 'are'} waiting on a human.`;

  return (
    <>
      <AdminPageHeader section="withdrawals" meta={meta} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <DropChip label="STATUS" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUSES} />
        </div>
        <DataTable
          columns={[
            {
              key: 'userEmail',
              label: 'USER',
              fr: 2,
              render: (w) => (
                <span
                  className={canSeePeople && w.userId ? 'cursor-pointer hover:underline' : ''}
                  onClick={canSeePeople && w.userId ? (e) => { e.stopPropagation(); openPerson(w.userId); } : undefined}
                >
                  {w.userEmail || w.userDisplayName || w.userId}
                </span>
              ),
            },
            { key: 'amountCents', label: 'AMOUNT', fr: 0.9, right: true, render: (w) => money(w.amountCents) },
            { key: 'feeCents', label: 'FEE', fr: 0.7, right: true, render: (w) => money(w.feeCents) },
            { key: 'netAmountCents', label: 'NET', fr: 0.9, right: true, render: (w) => money(w.netAmountCents) },
            { key: 'status', label: 'STATUS', fr: 1, render: (w) => String(w.status || '').toUpperCase() },
            { key: 'createdAt', label: 'DATE', fr: 0.9, render: (w) => shortDate(w.createdAt) },
          ]}
          rows={rows}
          loading={loading}
          hasActions={canApprove}
          actions={(w) => {
            // A failed row's money is already back in the wallet — nothing to do (a retry would pay twice).
            if (w.status === 'processing') return [
              { label: 'RESUME', variant: 'solid', onClick: () => retry(w) },
              { label: 'MARK COMPLETED', onClick: () => markCompleted(w) },
              { label: 'MARK FAILED', variant: 'danger', onClick: () => markFailed(w) },
            ];
            if (w.status === 'pending') return [{ label: 'MARK FAILED', variant: 'danger', onClick: () => markFailed(w) }];
            return [];
          }}
          total={total}
          page={page}
          pageSize={20}
          onPage={setPage}
        />
      </div>
    </>
  );
}
